import React, {ReactElement, ReactNode, useEffect, useState} from 'react';
import {classes, st} from '../../../common/components/Layout/Layout.st.css';
import {MainLoader} from '../../../common/components/Layout/MainLoader/MainLoader';
import {AsideLoader} from '../../../common/components/Layout/AsideLoader/AsideLoader';
import {HeaderLoader} from '../../../common/components/Layout/HeaderLoader/HeaderLoader';
import {useExperiments} from '@wix/yoshi-flow-editor';
import {A11yIds, SPECS} from '../../../common/constants';
import {useLocaleKeys} from '../../../locale-keys/LocaleKeys';

export enum LayoutDataHooks {
  mainSection = 'LayoutDataHooks.mainSection',
  oneColumnViewSummary = 'LayoutDataHooks.oneColumnViewSummary',
  twoColumnViewSummary = 'LayoutDataHooks.twoColumnViewSummary',
  verticalDivider = 'LayoutDataHooks.verticalDivider',
}

export interface LayoutProps {
  isLoading: boolean;
  showErrorState?: boolean;
  isOneColumnView?: boolean;
  Header: () => ReactNode;
  Aside: () => ReactNode;
  Footer: () => ReactNode;
  ErrorState: () => ReactNode;
  children: ReactNode;
  dataHook: string;
  dir: string;
}

const WrapWithDiv = ({className, children}: {className: string; children: ReactNode}) => {
  const {experiments} = useExperiments();

  /* istanbul ignore next */
  return experiments.enabled(SPECS.ecomCheckoutBrandStyling) ? (
    <div className={className}>{children}</div>
  ) : (
    (children as ReactElement)
  );
};

/* eslint-disable sonarjs/cognitive-complexity */
export const Layout = ({
  dataHook,
  dir,
  isLoading,
  showErrorState,
  isOneColumnView,
  Header,
  Aside,
  Footer,
  ErrorState,
  children,
}: LayoutProps) => {
  const localeKeys = useLocaleKeys();
  const [loaded, setLoaded] = useState(false);
  const {experiments} = useExperiments();
  const ecomCheckoutBrandStyling = experiments.enabled(SPECS.ecomCheckoutBrandStyling);
  /* istanbul ignore next */
  const mainClass = ecomCheckoutBrandStyling ? classes.main : classes.deprecatedMain;
  /* istanbul ignore next */
  const asideClass = ecomCheckoutBrandStyling ? classes.aside : classes.deprecatedAside;

  useEffect(() => {
    setLoaded(!isLoading);
  }, [isLoading]);

  return (
    <div
      data-hook={dataHook}
      className={st(classes.root, showErrorState ? classes.errorStateContainer : undefined)}
      dir={dir}>
      {loaded || showErrorState ? Header() : <HeaderLoader />}
      {showErrorState ? (
        ErrorState()
      ) : (
        <WrapWithDiv className={classes.mainContainer}>
          <div className={classes.container}>
            {isOneColumnView && (
              <WrapWithDiv className={classes.summaryContainer}>
                <aside data-hook={LayoutDataHooks.oneColumnViewSummary} className={asideClass}>
                  {loaded ? Aside() : null}
                </aside>
              </WrapWithDiv>
            )}
            <WrapWithDiv className={classes.formContainer}>
              <section
                className={mainClass}
                data-hook={LayoutDataHooks.mainSection}
                aria-label={localeKeys.checkout.checkoutForm.ariaLabel()}>
                {loaded ? children : <MainLoader />}
              </section>
            </WrapWithDiv>
            {!isOneColumnView && <div data-hook={LayoutDataHooks.verticalDivider} className={classes.divider} />}
            {(!isOneColumnView || !loaded) && (
              <WrapWithDiv className={classes.summaryContainer}>
                <section
                  aria-labelledby={A11yIds.SummarySection}
                  data-hook={LayoutDataHooks.twoColumnViewSummary}
                  className={asideClass}>
                  {loaded ? Aside() : <AsideLoader />}
                </section>
              </WrapWithDiv>
            )}
          </div>
        </WrapWithDiv>
      )}
      {loaded && Footer()}
    </div>
  );
};
